import { statusTagType } from '../components/statusTagType';
import { currentLanguage, formatDate, formatMoney, localeOf, localized, useTranslation } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAdmin } from '../adminContext';
import { ordersApi, type CustomerInfo, type LineRequest, type OrderSetup } from './ordersApi';

/** Where the order screens run: the store manages its orders; HO reads every store's (system rule 4). */
export type OrdersMode = 'store' | 'ho';

export const ORDER_READ_PERMISSION: Record<OrdersMode, string> = { store: 'store.orders.read', ho: 'ho.orders.read' };

export const ORDER_PERMISSIONS = {
  manage: 'store.orders.manage',
  discount: 'store.orders.discount',
  charge: 'store.charges.manage',
  delivery: 'store.deliveries.manage',
  lineVoid: 'store.orders.void_line',
  orderVoid: 'store.orders.void',
  record: 'store.payments.record',
  refund: 'store.payments.refund',
  /** A paid bill corrected the same day (ADR 0135); without it a manager approves. */
  correct: 'store.orders.correct',
} as const;

/** An order's four statuses (ADR 0088): the order itself, its payment, the kitchen and getting it to the customer. */
export const ORDER_STATUSES = ['open', 'completed', 'voided', 'merged', 'cancelled'] as const;
export const PAYMENT_STATUSES = ['unpaid', 'partially_paid', 'paid', 'overpaid'] as const;
export const KITCHEN_STATUSES = ['not_sent', 'partially_sent', 'sent'] as const;
export const FULFILMENT_STATUSES = ['waiting', 'ready', 'out_for_delivery', 'handed_over', 'delivered', 'served', 'failed'] as const;

export const ordersKey = ['orders'] as const;

export function useOrdersApi() {
  const { client } = useAdmin();
  return useMemo(() => ordersApi(client), [client]);
}

/** Amounts as the backend sent them; never calculated here (system rule 5). */
export function useFormat() {
  const { i18n } = useTranslation();
  const language = i18n.language;
  return useMemo(() => {
    const locale = localeOf(currentLanguage());
    const dateTime = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' });
    const time = new Intl.DateTimeFormat(locale, { timeStyle: 'short' });
    return {
      language,
      money: (amount: number | string | null | undefined, currency: string) =>
        amount == null ? '' : formatMoney(Number(amount), locale, currency),
      dateTime: (value: string | null | undefined) => (value ? dateTime.format(new Date(value)) : ''),
      number: (value: number | string) => new Intl.NumberFormat(locale).format(Number(value)),
      /** Time of day, 24-hour, in the browser's zone. */
      time: (value: string | Date | null | undefined) => (value ? time.format(new Date(value)) : ''),
      /** A calendar date such as a business date ("2026-10-04"). */
      date: (value: string | null | undefined) => (value ? formatDate(value, locale) : ''),
    };
  }, [language]);
}

/** POS setup for pickers and for naming ids (sale types, payment methods, reasons, charges). */
export function useOrderSetup() {
  const api = useOrdersApi();
  const setup = useQuery({ queryKey: [...ordersKey, 'setup'], queryFn: () => api.setup(), staleTime: 5 * 60_000 });
  const data: OrderSetup | undefined = setup.data;
  return useMemo(() => {
    const refName = (
      refs: readonly { id: string; code: string; name: string }[] | undefined,
      id: string | null | undefined,
    ) => {
      const ref = id ? refs?.find((r) => r.id === id) : undefined;
      return ref ? `${ref.code} · ${ref.name}` : undefined;
    };
    const names = (data?.names ?? {}) as Record<string, Record<string, string> | undefined>;
    const name = (id: string | null | undefined, fallback: string) => (id ? localized(names[id], fallback) : fallback);
    return {
      setup: data,
      saleTypeName: (id: string, code: string) => name(id, data?.saleTypes.find((s) => s.id === id)?.name ?? code),
      paymentMethodName: (id: string) => {
        const method = data?.paymentMethods.find((p) => p.id === id);
        return method ? name(id, method.name) : undefined;
      },
      reasonName: (id: string | null | undefined) => {
        const reason = data?.reasonCodes.find((r) => r.id === id);
        return reason ? name(reason.id, reason.name) : undefined;
      },
      /** The menu and price list an order was taken on (ADR 0017), as code and name. */
      menuName: (id: string | null | undefined) => refName(data?.menus, id),
      priceListName: (id: string | null | undefined) => refName(data?.priceLists, id),
      reasons: (type: string) =>
        data?.reasonCodes.filter((r) => r.reasonType === type).map((r) => ({ id: r.id, label: name(r.id, r.name) })) ??
        [],
      name,
    };
  }, [data]);
}

// The approval flow is shared with the POS (@pos/design-system).
export {
  isApprovalCancelled as isCancelled,
  useApprovalFlow,
  type ApprovalTarget,
  type PendingApproval,
} from '@pos/design-system';

// ---- drafts and customer fields

/** An item the user has chosen but not saved yet. Prices shown are list prices; the order total comes from the server. */
export interface LineDraft {
  key: string;
  itemId: string;
  name: string;
  quantity: number;
  /** The unit sold (ADR 0030); `unitName` is shown only when it is not the item's base unit. */
  uomId?: string | null;
  unitName?: string | null;
  modifierIds: string[];
  modifierLabels: string[];
  note: string;
  openPrice: number | null;
  unitPrice: number | null;
  /** The menu's currency, for showing the list price. */
  currency: string;
}

export const toLineRequest = (draft: LineDraft): LineRequest => ({
  itemId: draft.itemId,
  uomId: draft.uomId ?? null,
  quantity: draft.quantity,
  modifierIds: draft.modifierIds,
  note: draft.note || null,
  openPrice: draft.openPrice,
});

/** Accent-insensitive search text; shared with the POS. */
export { fold } from '@pos/i18n';

export const emptyCustomer: CustomerInfo = {
  name: null,
  phone: null,
  province: null,
  ward: null,
  street: null,
  note: null,
};

/** Nothing typed means no customer. */
export const customerOrNull = (customer: CustomerInfo) => (Object.values(customer).some((v) => v) ? customer : null);

// ---- transaction lists

/** A local date range (yyyy-mm-dd, both inclusive) as the UTC instants the API filters on. */
export function dateRange(from: string, to: string) {
  const start = (date: string) => new Date(`${date}T00:00:00`);
  return {
    from: from ? start(from).toISOString() : undefined,
    to: to ? new Date(start(to).getTime() + 86_400_000).toISOString() : undefined,
  };
}

/** Today in the browser's time zone, yyyy-mm-dd: the lists open on today's transactions. */
export const today = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};

/** Every status a delivery can have (ADR 0025), in the order a delivery goes through them. */
export const DELIVERY_STATUSES = [
  'pending',
  'searching_driver',
  'no_driver',
  'assigned',
  'picked_up',
  'out_for_delivery',
  'delivered',
  'failed',
  'cancel_requested',
  'cancelled',
  'returned',
] as const;

/** A delivery that is over: nothing more will happen to it. */
export const DELIVERY_FINAL = ['delivered', 'failed', 'cancelled', 'returned'];

/** @deprecated Use `StatusTag` / `statusTagType` from the components; kept for ho-admin. */
export const deliveryTag = (status: string) => statusTagType(status);
